/* eslint-disable no-await-in-loop */
import { Services } from '@simplybenefits/sb-ui-library';

/**
* Validates the premium illustration data and returns an object containing the validation errors for each benefit type and class.
* @param {Object} data Containing premium illustration data to be validated.
* @returns {Object} 
*/
const _validatePremiumIllustrations = async (data) => {
    const premiumIllustrationErrors = {};

    for (const [ benefitType, benefitClasses ] of Object.entries(data.premiumIllustration)) {
        premiumIllustrationErrors[benefitType] = {};
    
        for (const [ classID, classData ] of Object.entries(benefitClasses)) {
            const validationPromises = [
                Services.validateInput('carrierChangePercentage', classData.carrierChangePercentage),
                Services.validateInput('percentage', classData.discountPercentage),
                Services.validateInput('percentage', classData.commissionPercentage),
                Services.validateInput('percentage', classData.markupPercentage),
                Services.validateInput('checkEmpty', classData.carrier)
            ];
    
            if ([ 'life', 'depLife', 'ci', 'ciSpouse', 'ciChild', 'add', 'outOfCountry', 'ltd', 'std' ].includes(benefitType)) {
                validationPromises.push(
                    Services.validateInput('number', classData.numberOfPaidClaims),
                    Services.validateInput('number', classData.totalAmountOfClaimsPaid)
                );
            } else {
                validationPromises.push(null, null); // Add placeholders for consistency
            }

            const [
                carrierChangePercentage,
                discountPercentage,
                commissionPercentage,
                markupPercentage,
                carrier,
                numberOfPaidClaims,
                totalAmountOfClaimsPaid
            ] = await Promise.all(validationPromises);
    
    
            premiumIllustrationErrors[benefitType][classID] = {
                carrierChangePercentage,
                discountPercentage,
                commissionPercentage,
                markupPercentage,
                carrier,
                numberOfPaidClaims: numberOfPaidClaims,
                totalAmountOfClaimsPaid: totalAmountOfClaimsPaid
            };
        }
    }

    return premiumIllustrationErrors;
};

/**
* Validates the experience analysis classes in the provided data and returns an object containing the validation errors for each class.
* @param {Object} data Containing experience analysis classes to be validated.
* @returns {Object}
*/
const _validateExperienceAnalysisClasses = async (data) => {
    const experienceAnalysisClassErrors = {};

    for (const [ classID, classData ] of Object.entries(data.experienceAnalysis.classes)) {
        const validationPromises = [
            Services.validateInput('percentage', classData.ehcStopLossChargePercentage),
            Services.validateInput('number', classData.ehcStopLossClaims)
        ];

        const [
            ehcStopLossChargePercentage,
            ehcStopLossClaims
        ] = await Promise.all(validationPromises);
    
        experienceAnalysisClassErrors[classID] = {
            ehcStopLossChargePercentage,
            ehcStopLossClaims
        };
    }
    
    return experienceAnalysisClassErrors;
    
};

/**
 * Validates the historical experience analysis data.
 * @param {Object} data The full form data containing historical experience analysis.
 * @returns {Object} An object containing validation errors for each year, class, and benefit.
 */
const _validateHistoricalExperienceAnalysis = async (data) => {
    const historicalErrors = {};
    const historicalData = data.experienceAnalysis.historicalExperienceAnalysis;

    // Iterate through each Year
    for (const [ year, yearData ] of Object.entries(historicalData)) {
        historicalErrors[year] = {};

        // Filter out non-class keys before looping
        const classIDs = Object.keys(yearData).filter((key) => ![ 'periodStart', 'periodEnd' ].includes(key));

        for (const classID of classIDs) {
            const classData = yearData[classID];
            historicalErrors[year][classID] = {};

            // Iterate through each Benefit Type (ehc, dental, std)
            for (const benefitType of [ 'ehc', 'dental', 'std' ]) {
                const benefitData = classData[benefitType];

                // Define the fields to validate for this benefit
                const validationPromises = [
                    Services.validateInput('number', benefitData.premium),
                    Services.validateInput('number', benefitData.annualizedPremium),
                    Services.validateInput('number', benefitData.paidClaims),
                    Services.validateInput('number', benefitData.stopLossClaims),
                    Services.validateInput('number', benefitData.changeInIbnr),
                    Services.validateInput('number', benefitData.stopLossCharge),
                    Services.validateInput('number', benefitData.incurredClaims),
                    Services.validateInput('number', benefitData.projectedClaimsAnnualized),
                    Services.validateInput('percentage', benefitData.paidLossRatio),
                    Services.validateInput('percentage', benefitData.adjustedLossRatio)
                ];

                const [
                    premium,
                    annualizedPremium,
                    paidClaims,
                    stopLossClaims,
                    changeInIbnr,
                    stopLossCharge,
                    incurredClaims,
                    projectedClaimsAnnualized,
                    paidLossRatio,
                    adjustedLossRatio
                ] = await Promise.all(validationPromises);

                historicalErrors[year][classID][benefitType] = {
                    premium,
                    annualizedPremium,
                    paidClaims,
                    stopLossClaims,
                    changeInIbnr,
                    stopLossCharge,
                    incurredClaims,
                    projectedClaimsAnnualized,
                    paidLossRatio,
                    adjustedLossRatio
                };
            }
        }
    }

    return historicalErrors;
};

/**
 * Validates the given form data by checking individual fields and running specific validation rules.
 * @param {Object} data The form data to be validated.
 */
const validateForm = async (data) => {
    // General
    const salesRepName = await Services.validateInput('name', data.general.salesRepName);
    const salesRepEmail = await Services.validateInput('email', data.general.salesRepEmail);
    const salesRepPhone = await Services.validateInput('phone', data.general.salesRepPhone);

    // Financial Information
    const rateCap = data.financialInformation.rateCap ? await Services.validateInput('percentage', data.financialInformation.rateCap) : null;
    const rateCapConditions = data.financialInformation.rateCapConditions ? await Services.validateInput('checkEmpty', data.financialInformation.rateCapConditions) : null;

    // Experience Analysis
    const ehcTargetLossRatioPercentage = await Services.validateInput('percentage', data.experienceAnalysis.ehc.targetLossRatioPercentage);
    const dentalTargetLossRatioPercentage = await Services.validateInput('percentage', data.experienceAnalysis.dental.targetLossRatioPercentage);
    const stdTargetLossRatioPercentage = await Services.validateInput('percentage', data.experienceAnalysis.std.targetLossRatioPercentage);

    const ehcIbnrRatePercentage = await Services.validateInput('percentage', data.experienceAnalysis.ehc.ibnrRatePercentage);
    const dentalIbnrRatePercentage = await Services.validateInput('percentage', data.experienceAnalysis.dental.ibnrRatePercentage);
    const stdIbnrRatePercentage = await Services.validateInput('percentage', data.experienceAnalysis.std.ibnrRatePercentage);

    const ehcIbnrRatePreviousPercentage = await Services.validateInput('number', data.experienceAnalysis.ehc.ibnrRatePreviousPercentage);
    const dentalIbnrRatePreviousPercentage = await Services.validateInput('number', data.experienceAnalysis.dental.ibnrRatePreviousPercentage);
    const stdIbnrRatePreviousPercentage = await Services.validateInput('number', data.experienceAnalysis.std.ibnrRatePreviousPercentage);

    const ehcTrend = await Services.validateInput('percentage', data.experienceAnalysis.ehc.trend);
    const dentalTrend = await Services.validateInput('percentage', data.experienceAnalysis.dental.trend);

    const ehcCredibility = await Services.validateInput('percentage', data.experienceAnalysis.ehc.credibility);
    const dentalCredibility = await Services.validateInput('percentage', data.experienceAnalysis.dental.credibility);
    const stdCredibility = await Services.validateInput('percentage', data.experienceAnalysis.std.credibility);

    const ehcStopLossChargeDefinition = await Services.validateInput('checkEmpty', data.experienceAnalysis.ehc.stopLossChargeDefinition);

    const experienceAnalysisClassErrors = await _validateExperienceAnalysisClasses(data);

    const historicalExperienceAnalysisErrors = await _validateHistoricalExperienceAnalysis(data);

    // Premium Illustration
    const premiumIllustrationErrors = await _validatePremiumIllustrations(data);

    const validationErrors = {
        general: {
            salesRepName,
            salesRepEmail,
            salesRepPhone
        },
        financialInformation: {
            rateCap,
            rateCapConditions
        },
        experienceAnalysis: {
            ehc: {
                targetLossRatioPercentage: ehcTargetLossRatioPercentage,
                ibnrRatePercentage: ehcIbnrRatePercentage,
                ibnrRatePreviousPercentage: ehcIbnrRatePreviousPercentage,
                trend: ehcTrend,
                credibility: ehcCredibility,
                stopLossChargeDefinition: ehcStopLossChargeDefinition
            },
            dental: {
                targetLossRatioPercentage: dentalTargetLossRatioPercentage,
                ibnrRatePercentage: dentalIbnrRatePercentage,
                ibnrRatePreviousPercentage: dentalIbnrRatePreviousPercentage,
                trend: dentalTrend,
                credibility: dentalCredibility
            },
            std: {
                targetLossRatioPercentage: stdTargetLossRatioPercentage,
                ibnrRatePercentage: stdIbnrRatePercentage,
                ibnrRatePreviousPercentage: stdIbnrRatePreviousPercentage,
                credibility: stdCredibility
            },
            classes: experienceAnalysisClassErrors,
            historicalExperienceAnalysis: historicalExperienceAnalysisErrors
        },
        premiumIllustration: premiumIllustrationErrors
    };

    const flattenedErrors = [
        salesRepName,
        salesRepEmail,
        salesRepPhone,
        rateCap,
        rateCapConditions,
        ehcTargetLossRatioPercentage,
        dentalTargetLossRatioPercentage,
        stdTargetLossRatioPercentage,
        ehcIbnrRatePercentage,
        dentalIbnrRatePercentage,
        stdIbnrRatePercentage,
        ehcIbnrRatePreviousPercentage,
        dentalIbnrRatePreviousPercentage,
        stdIbnrRatePreviousPercentage,
        ehcTrend,
        dentalTrend,
        ehcCredibility,
        dentalCredibility,
        stdCredibility,
        ehcStopLossChargeDefinition,
        ...Object.values(experienceAnalysisClassErrors).flatMap((classData) => classData ? Object.values(classData) : []), // Flatten single depth object
        ...Object.values(premiumIllustrationErrors) // Flatten double depth objects
            .flatMap((benefitClasses) => benefitClasses ? Object.values(benefitClasses) : [])
            .flatMap((classData) => classData ? Object.values(classData) : []),
        ...Object.values(historicalExperienceAnalysisErrors)
            .flatMap((yearErrors) => yearErrors ? Object.values(yearErrors) : [])
            .flatMap((classErrors) => classErrors ? Object.values(classErrors) : [])
            .flatMap((benefitErrors) => benefitErrors ? Object.values(benefitErrors) : [])
    ];

    // Display validation error message if error was found
    const hasErrors = flattenedErrors.some((error) => error);

    return { hasErrors, validationErrors };
};

export default validateForm;